import { z } from 'zod';

// Mirrors parking-app-api internal/analytics/query (contract.go, model.go,
// view.go) and internal/analytics_query_handler.go. Go encodes nil slices as
// null, so every list accepts null and reads as empty.
const list = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((value) => value ?? []);

export const ANALYTICS_RANGE_PRESETS = [
  'today',
  'last_7d',
  'last_30d',
  'last_90d',
] as const;
// POST /analytics/query refuses more queries than this in one request.
export const ANALYTICS_MAX_BATCH = 24;

export type AnalyticsRangePreset = (typeof ANALYTICS_RANGE_PRESETS)[number];

export type AnalyticsGranularity = 'hour' | 'day' | 'week' | 'month';
export type AnalyticsFilterOp = 'eq' | 'in';

export interface AnalyticsFilter {
  field: string;
  op: AnalyticsFilterOp;
  values: string[];
}

// A mode string, or explicit dates which the server reads as a custom compare.
export type AnalyticsCompare =
  | 'prior_period'
  | 'last_year'
  | { start: string; end: string };

// One widget query. There is no customer id here by design: the server derives
// scope from the token, and location_ids only narrows it. A time-ranged source
// takes `range` or start_date and end_date; a snapshot source takes neither.
export interface AnalyticsWidgetQuery {
  source: string;
  metric: string;
  dimensions?: string[];
  filters?: AnalyticsFilter[];
  range?: AnalyticsRangePreset;
  start_date?: string;
  end_date?: string;
  granularity?: AnalyticsGranularity;
  time_basis?: string;
  order_by?: { metric: string; descending?: boolean };
  timezone: string;
  compare?: AnalyticsCompare;
  location_ids?: string[];
}

// A reason the portal does not know yet reads as a permission refusal.
export const AnalyticsForbiddenReasonSchema = z
  .enum([
    'permission',
    'location',
    'customer',
    'internal',
    'product_not_entitled',
  ])
  .catch('permission');
export type AnalyticsForbiddenReason = z.infer<
  typeof AnalyticsForbiddenReasonSchema
>;

// A named lot the query left out instead of refusing the whole answer.
const AnalyticsExcludedLocationSchema = z.object({
  location_id: z.string(),
  reason: AnalyticsForbiddenReasonSchema,
});
export type AnalyticsExcludedLocation = z.infer<
  typeof AnalyticsExcludedLocationSchema
>;

// Change from the comparison value of the same group. Percent is null when the
// comparison value is zero, and both are null when either value is undefined.
const AnalyticsDeltaSchema = z.object({
  absolute: z.number().nullable(),
  percent: z.number().nullable(),
});
export type AnalyticsDelta = z.infer<typeof AnalyticsDeltaSchema>;

const AnalyticsRowSchema = z.object({
  dimensions: z
    .record(z.string())
    .nullish()
    .transform((v) => v ?? {}),
  value: z.number().nullable(),
  // Set on the current rows of a compared query, by metric id.
  deltas: z.record(AnalyticsDeltaSchema).nullish(),
});
export type AnalyticsRow = z.infer<typeof AnalyticsRowSchema>;

// A status the portal does not know yet degrades that one widget to an error
// instead of failing the whole batch.
export const AnalyticsWidgetResultSchema = z
  .discriminatedUnion('status', [
    z.object({
      status: z.literal('ok'),
      location_ids: list(z.string()),
      rows: list(AnalyticsRowSchema),
      compare_rows: list(AnalyticsRowSchema),
      is_per_day: z.boolean().optional().default(false),
      excluded_locations: z.array(AnalyticsExcludedLocationSchema).nullish(),
    }),
    z.object({
      status: z.literal('no_data'),
      excluded_locations: z.array(AnalyticsExcludedLocationSchema).nullish(),
    }),
    z.object({
      status: z.literal('forbidden'),
      reason: AnalyticsForbiddenReasonSchema.optional(),
    }),
    z.object({
      status: z.literal('error'),
      code: z.number().int().optional(),
      message: z.string().optional(),
    }),
  ])
  .catch({ status: 'error', message: 'Unsupported result' });
export type AnalyticsWidgetResult = z.infer<typeof AnalyticsWidgetResultSchema>;

export const AnalyticsQueryBatchResponseSchema = z.object({
  results: list(AnalyticsWidgetResultSchema),
});
export type AnalyticsQueryBatchResponse = z.infer<
  typeof AnalyticsQueryBatchResponseSchema
>;

const LockSchema = z.object({ reason: AnalyticsForbiddenReasonSchema });

const AnalyticsMetricViewSchema = z.object({
  id: z.string(),
  label: z.string(),
  tier: z.string(),
  format: z.string().optional(),
  required_permission: z.string().optional(),
  supported_viz: list(z.string()),
  is_daily_average: z.boolean().optional().default(false),
  lock: LockSchema.nullish(),
});

const AnalyticsSourceViewSchema = z.object({
  id: z.string(),
  is_snapshot: z.boolean(),
  lock: LockSchema.nullish(),
  metrics: list(AnalyticsMetricViewSchema),
  dimensions: list(z.object({ id: z.string(), is_time: z.boolean() })),
  filters: list(
    z.object({
      id: z.string(),
      // Unknown operators are kept as text so a new one never breaks the catalog.
      operators: list(z.string()),
      values: list(z.string()),
    })
  ),
});

export const AnalyticsCatalogSchema = z.object({
  products: list(
    z.object({
      product: z.string(),
      sources: list(AnalyticsSourceViewSchema),
    })
  ),
  location_ids: list(z.string()),
  // Planned metrics no query can name yet.
  coming_soon: list(
    z.object({ id: z.string(), label: z.string(), product: z.string() })
  ),
});
export type AnalyticsCatalog = z.infer<typeof AnalyticsCatalogSchema>;
